import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";

const LOGO = "/logo.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6">
      <div className="w-full max-w-md">
        <img src={LOGO} alt="Dermatrix" className="mb-8 h-12 object-contain" />
        <h1 className="font-display text-3xl font-semibold text-navy">Set a new password</h1>

        {!token ? (
          <p className="mt-4 text-sm text-red-700" data-testid="reset-no-token">
            This reset link is invalid. Request a new one from the{" "}
            <Link to="/forgot-password" className="font-medium underline">forgot password</Link> page.
          </p>
        ) : done ? (
          <div className="mt-6 flex items-start gap-3 rounded-lg bg-white p-4 shadow-sm" data-testid="reset-done">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
            <p className="text-sm text-ink">
              Password updated. <Link to="/login" className="font-medium text-navy underline">Sign in</Link> with your new password.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-5" data-testid="reset-form">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-ink">New password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value.replace(/\s/g, ""))}
                data-testid="reset-password" required minLength={8} className="h-11 bg-white" placeholder="At least 8 characters" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm" className="text-ink">Confirm new password</Label>
              <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\s/g, ""))}
                data-testid="reset-confirm" required minLength={8} className="h-11 bg-white" placeholder="••••••••" />
            </div>
            {error && (
              <div className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="reset-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={loading} data-testid="reset-submit"
              className="h-11 w-full bg-navy text-white hover:bg-navy-hover">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</> : "Reset password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
